import toast from "react-hot-toast";

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};
  const validationErrors = responseData?.errors;
  let firstValidationMessage = null;

  if (validationErrors && typeof validationErrors === "object") {
    const firstFieldErrors = Object.values(validationErrors)[0];
    if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
      firstValidationMessage = firstFieldErrors[0];
    } else if (typeof firstFieldErrors === "string") {
      firstValidationMessage = firstFieldErrors;
    }
  }

  return (
    responseData?.message ||
    responseData?.error?.message ||
    firstValidationMessage ||
    error?.message ||
    fallbackMessage
  );
};

/**
 * Common Fetch Data Utility - Reusable data fetching with error handling
 * Following functional approach for MVC architecture
 */

/**
 * Generic fetch data function with loading state management
 * @param {Function} apiCall - API service function to call
 * @param {Object} params - Parameters to pass to API
 * @param {Object} stateSetters - Object containing state setter functions
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Result object with success status and data
 */
export const fetchDataWithLoading = async (
  apiCall,
  params = {},
  stateSetters = {},
  options = {},
  id = null
) => {
  const { setLoading, setData, setTotalData, setError } = stateSetters;

  const {
    successMessage = "Data fetched successfully!",
    errorMessage = "Failed to fetch data!",
    showSuccessToast = false,
    showErrorToast = true,
    dataPath = "data",
    totalPath = "meta.total",
  } = options;

  try {
    // Set loading state
    if (setLoading) setLoading(true);
    if (setError) setError(null);

    // Make API call
    let response;
    if (apiCall) {
      if (id) {
        response = await apiCall(id, params);
      } else {
        response = await apiCall(params);
      }
    }

    // Extract data based on path
    const extractedData = dataPath
      ? getNestedValue(response, dataPath)
      : response;
    const total = totalPath ? getNestedValue(response, totalPath) : null;

    // Update state
    if (setData) setData(extractedData);
    if (setTotalData && total !== null) setTotalData(total);
    if (setLoading) setLoading(false);

    // Show success message if needed
    if (showSuccessToast && successMessage) {
      toast.success(successMessage);
    }

    return {
      success: true,
      data: extractedData,
      total,
      response,
    };
  } catch (error) {
    // Handle error
    if (setLoading) setLoading(false);
    if (setError) setError(error);

    // Extract error message from different possible response structures
    const errorMsg = getApiErrorMessage(error, errorMessage);

    if (showErrorToast) {
      toast.error(errorMsg);
    }

    if (process.env.NODE_ENV === 'development') {
      console.error("Fetch error:", error);
    }

    return {
      success: false,
      error,
      message: errorMsg,
    };
  }
};

/**
 * Generic CRUD operations with common error handling
 */
export const crudOperations = {
  /**
   * Create operation
   * @param {Function} createApi - Create API function
   * @param {Object} data - Data to create
   * @param {Object} options - Options
   * @returns {Promise<Object>} Result
   */
  create: async (createApi, data, options = {}) => {
    const {
      successMessage = "Created successfully!",
      errorMessage = "Failed to create!",
      onSuccess,
      onError,
    } = options;

    try {
      const response = await createApi(data);
      // Show success message
      if (successMessage) {
        toast.success(successMessage);
      }
      onSuccess && onSuccess(response);
      return { success: true, data: response };
    } catch (error) {
      // Extract error message from different possible response structures
      const errorMsg = getApiErrorMessage(error, errorMessage);

      toast.error(errorMsg);
      onError && onError(error);
      return { success: false, error, message: errorMsg };
    }
  },

  /**
   * Update operation
   * @param {Function} updateApi - Update API function
   * @param {Object} data - Data to update
   * @param {string} id - ID to update
   * @param {Object} options - Options
   * @returns {Promise<Object>} Result
   */
  update: async (updateApi, data, id, options = {}) => {
    const {
      successMessage = "Updated successfully!",
      errorMessage = "Failed to update!",
      onSuccess,
      onError,
    } = options;

    try {
      const response = await updateApi(data, id);
      // Show success message
      if (successMessage) {
        toast.success(successMessage);
      }
      onSuccess && onSuccess(response);
      return { success: true, data: response };
    } catch (error) {
      // Extract error message from different possible response structures
      const errorMsg = getApiErrorMessage(error, errorMessage);

      toast.error(errorMsg);
      onError && onError(error);
      return { success: false, error, message: errorMsg };
    }
  },

  /**
   * Delete operation
   * @param {Function} deleteApi - Delete API function
   * @param {string} id - ID to delete
   * @param {Object} options - Options
   * @returns {Promise<Object>} Result
   */
  delete: async (deleteApi, id, options = {}) => {
    const {
      successMessage = "Deleted successfully!",
      errorMessage = "Failed to delete!",
      onSuccess,
      onError,
    } = options;

    try {
      await deleteApi(id);
      // Show success message
      if (successMessage) {
        toast.success(successMessage);
      }
      onSuccess && onSuccess();
      return { success: true };
    } catch (error) {
      // Extract error message from different possible response structures
      const errorMsg = getApiErrorMessage(error, errorMessage);

      toast.error(errorMsg);
      onError && onError(error);
      return { success: false, error, message: errorMsg };
    }
  },
};

/**
 * Debounced search utility
 * @param {Function} searchFunction - Function to call after debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const createDebouncedSearch = (searchFunction, delay = 500) => {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      searchFunction(...args);
    }, delay);
  };
};

/**
 * Helper function to get nested object values
 * @param {Object} obj - Object to search
 * @param {string} path - Dot notation path
 * @returns {*} Value at path
 */
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Common state management for list pages
 * @param {Object} initialState - Initial state object
 * @returns {Object} State management functions
 */
export const createListStateManager = (initialState = {}) => {
  const defaultState = {
    loading: false,
    data: [],
    total: 0,
    currentPage: 1,
    search: "",
    selectedId: "",
    action: "",
    error: null,
    ...initialState,
  };

  let state = { ...defaultState };

  const stateSetters = {
    setLoading: (loading) => {
      state.loading = loading;
    },
    setData: (data) => {
      state.data = data;
    },
    setTotal: (total) => {
      state.total = total;
    },
    setCurrentPage: (page) => {
      state.currentPage = page;
    },
    setSearch: (search) => {
      state.search = search;
    },
    setSelectedId: (id) => {
      state.selectedId = id;
    },
    setAction: (action) => {
      state.action = action;
    },
    setError: (error) => {
      state.error = error;
    },
    resetState: () => {
      state = { ...defaultState };
    },
    getState: () => ({ ...state }),
  };

  return stateSetters;
};

export default {
  fetchDataWithLoading,
  crudOperations,
  createDebouncedSearch,
  createListStateManager,
};
